import { useState, useEffect, useRef } from 'react'
import { X, Lock, Pencil, Trash2, Lock as FiscalLock, Download, Upload, Paperclip, FileText, AlertCircle, Loader2 } from 'lucide-react'
import { useLedger } from '../lib/LedgerContext'
import { formatCurrency, formatDate } from '../lib/format'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../lib/ToastContext'
import { uploadFile, getSignedUrlByPath } from '../lib/storage'
import { supabase } from '../lib/supabase'
import { categories } from '../data/seed'

const TIME_WINDOW_MS = 30 * 60 * 1000
const FISCAL_LOCK_MS = 365 * 24 * 60 * 60 * 1000

const FILTERS = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Half-Year', 'Yearly']

function remainingMs(createdAt, locked) {
  if (locked) return 0
  return Math.max(0, TIME_WINDOW_MS - (Date.now() - createdAt))
}

function fmtCountdown(ms) {
  const total = Math.ceil(ms / 1000)
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function withinFilter(txnDate, filter) {
  const d = new Date(txnDate)
  const now = new Date()
  const diff = now - d
  const day = 24 * 60 * 60 * 1000
  const ranges = {
    Daily: 1 * day,
    Weekly: 7 * day,
    Monthly: 30 * day,
    Quarterly: 90 * day,
    'Half-Year': 182 * day,
    Yearly: 365 * day,
  }
  return diff <= (ranges[filter] || Infinity)
}

function isFiscallyLocked(txnDate) {
  return Date.now() - new Date(txnDate).getTime() > FISCAL_LOCK_MS
}

function escapeCsv(value) {
  if (value == null) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

export default function LedgerOverlay({ 
  open, 
  onClose, 
  role, 
  currency = 'NGN', 
  dateFormat = 'DD/MM/YYYY',
  isPageMode = false,  // ✅ When true, renders as a normal page (not overlay)
}) {
  const { transactions, editTransaction, deleteTransaction, lockTransaction, addTransaction } = useLedger()
  const { company } = useAuth()
  const { toast } = useToast()
  const fileRef = useRef(null)

  const [, force] = useState(0)
  const [filter, setFilter] = useState('Yearly')
  const [correction, setCorrection] = useState(null)
  const [correctionForm, setCorrectionForm] = useState({ type: 'Full Reversal', reason: '', amount: '' })
  const [correctionSaving, setCorrectionSaving] = useState(false)
  const [receiptFile, setReceiptFile] = useState(null)
  const [addForm, setAddForm] = useState({
    description: '', amount: '', type: 'expense', category: categories[0], date: new Date().toISOString().slice(0, 10),
  })
  const [addSaving, setAddSaving] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)

  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), 1000)
    return () => clearInterval(t)
  }, [])

  // ✅ Prevent body scroll when overlay is open (only for overlay mode)
  useEffect(() => {
    if (!isPageMode && open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open, isPageMode])

  if (!open) return null

  const filtered = transactions.filter((t) => withinFilter(t.date, filter))

  const exportCsv = () => {
    const headers = ['Ref', 'Date', 'Description', 'Category', 'Type', 'Amount', 'Currency', 'Receipt', 'Status']
    const rows = filtered.map((t) => [
      t.id,
      t.date,
      t.description,
      t.category || '',
      t.type || (t.amount >= 0 ? 'Income' : 'Expense'),
      t.amount,
      currency,
      t.receipt_url ? 'Attached' : '',
      t.locked ? 'Locked' : 'Active',
    ])
    const csv = [headers, ...rows].map((r) => r.map(escapeCsv).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ledger-export-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast('Ledger exported to CSV.', 'success')
  }

  const openCorrection = (txn) => {
    setCorrection(txn)
    setCorrectionForm({ type: 'Full Reversal', reason: '', amount: '' })
  }

  const submitCorrection = async (e) => {
    e.preventDefault()
    if (!correction) return
    if (!correctionForm.reason.trim()) {
      toast('A correction reason is required for audit compliance.', 'error')
      return
    }
    if (correctionForm.type === 'Partial Adjustment' && !correctionForm.amount) {
      toast('Partial adjustment requires an amount.', 'error')
      return
    }
    setCorrectionSaving(true)
    try {
      let offsetAmount
      let offsetDescription
      const originalAbs = Math.abs(correction.amount)
      if (correctionForm.type === 'Full Reversal') {
        offsetAmount = -correction.amount
        offsetDescription = `CORRECTION (Full Reversal) — Ref: ${correction.id} | Reason: ${correctionForm.reason.trim()}`
      } else {
        const adj = parseFloat(correctionForm.amount)
        offsetAmount = correction.amount >= 0 ? -Math.abs(adj) : Math.abs(adj)
        offsetDescription = `CORRECTION (Partial Adjustment) — Ref: ${correction.id} | Reason: ${correctionForm.reason.trim()}`
      }

      let receiptUrl = null
      if (receiptFile && company?.id) {
        try {
          const uploaded = await uploadFile(receiptFile, company.id, 'corrections', `${correction.id}-${Date.now()}-${receiptFile.name}`)
          receiptUrl = uploaded.path
        } catch (err) {
          console.error('Receipt upload failed:', err.message)
        }
      }

      addTransaction({
        description: offsetDescription,
        amount: offsetAmount,
        date: new Date().toISOString().slice(0, 10),
        category: 'Other',
        type: offsetAmount >= 0 ? 'Income' : 'Expense',
        reference: correction.id,
        receipt_url: receiptUrl,
        correctionOf: correction.id,
        correctionType: correctionForm.type,
        correctionReason: correctionForm.reason.trim(),
      })

      lockTransaction(correction.id)

      toast('Correction journal entry posted. Original transaction locked.', 'success')
      setCorrection(null)
      setReceiptFile(null)
      setCorrectionForm({ type: 'Full Reversal', reason: '', amount: '' })
    } catch (err) {
      toast('Failed to post correction: ' + (err.message || 'Unknown error'), 'error')
    } finally {
      setCorrectionSaving(false)
    }
  }

  const handleAddTransaction = async (e) => {
    e.preventDefault()
    if (!addForm.description.trim() || !addForm.amount) {
      toast('Description and amount are required.', 'error')
      return
    }
    setAddSaving(true)
    try {
      const amt = parseFloat(addForm.amount)
      let receiptUrl = null
      if (receiptFile && company?.id) {
        try {
          const uploaded = await uploadFile(receiptFile, company.id, 'receipts', `${Date.now()}-${receiptFile.name}`)
          receiptUrl = uploaded.path
        } catch (err) {
          console.error('Receipt upload failed:', err.message)
        }
      }
      addTransaction({
        description: addForm.description.trim(),
        amount: addForm.type === 'income' ? Math.abs(amt) : -Math.abs(amt),
        date: addForm.date,
        category: addForm.category,
        type: addForm.type === 'income' ? 'Income' : 'Expense',
        receipt_url: receiptUrl,
      })
      setAddForm({ description: '', amount: '', type: 'expense', category: categories[0], date: new Date().toISOString().slice(0, 10) })
      setReceiptFile(null)
      setShowAddForm(false)
      toast('Transaction saved with receipt.', 'success')
    } catch (err) {
      toast('Failed to save transaction.', 'error')
    } finally {
      setAddSaving(false)
    }
  }

  const viewReceipt = async (txn) => {
    if (!txn.receipt_url) {
      toast('No receipt attached to this transaction.', 'error')
      return
    }
    try {
      const url = await getSignedUrlByPath(txn.receipt_url, 60)
      if (url) window.open(url, '_blank')
      else toast('Could not generate receipt link.', 'error')
    } catch (err) {
      toast('Could not access receipt.', 'error')
    }
  }

  // ============================================================
  // RENDER LEDGER CONTENT (shared between page and overlay)
  // ============================================================
  const renderLedgerContent = () => (
    <>
      <div className="border-b border-neutral-200 bg-surface px-4 sm:px-6 py-3 overflow-x-auto">
        <div className="flex border border-neutral-200 w-fit">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 text-xs font-semibold transition-colors ${
                filter === f ? 'bg-brand text-white' : 'text-neutral-500 hover:text-ink'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          {filter === 'Yearly' && (
            <div className="border border-neutral-300 bg-neutral-50 px-4 py-3 flex items-center gap-2 mb-5">
              <FiscalLock size={15} className="text-neutral-400 shrink-0" />
              <p className="text-xs text-muted">
                Fiscal Year Sealed: Past History Locked for Governance and Institutional Audit Integrity.
              </p>
            </div>
          )}

          {role === 'admin' && showAddForm && (
            <form onSubmit={handleAddTransaction} className="surface p-6 mb-5 space-y-4 animate-fade-in">
              <h3 className="text-sm font-semibold text-ink">Add Transaction Entry</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="label-above">Type</label>
                  <select value={addForm.type} onChange={(e) => setAddForm({ ...addForm, type: e.target.value })} className="input-flat">
                    <option value="expense">Expense</option>
                    <option value="income">Income</option>
                  </select>
                </div>
                <div>
                  <label className="label-above">Amount</label>
                  <input type="number" step="0.01" value={addForm.amount} onChange={(e) => setAddForm({ ...addForm, amount: e.target.value })} className="input-flat font-mono" placeholder="0" min="0" />
                </div>
                <div>
                  <label className="label-above">Category</label>
                  <select value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} className="input-flat">
                    {categories.map((c) => (<option key={c}>{c}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-above">Description</label>
                  <input type="text" value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} className="input-flat" placeholder="e.g. Client invoice payment" />
                </div>
                <div>
                  <label className="label-above">Date</label>
                  <input type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} className="input-flat font-mono" />
                </div>
              </div>
              <div>
                <label className="label-above">Receipt Attachment</label>
                <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={(e) => setReceiptFile(e.target.files[0])} className="hidden" id="receipt-upload-overlay" />
                <button type="button" onClick={() => fileRef.current?.click()} className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2">
                  <Upload size={13} /> {receiptFile ? receiptFile.name : 'Attach receipt (optional)'}
                </button>
                {receiptFile && (
                  <button type="button" onClick={() => { setReceiptFile(null); if (fileRef.current) fileRef.current.value = '' }} className="ml-2 text-xs text-rose-600 hover:text-rose-700">
                    Remove
                  </button>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => { setShowAddForm(false); setReceiptFile(null) }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary flex items-center gap-1.5" disabled={addSaving}>
                  {addSaving ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />} Save Transaction
                </button>
              </div>
            </form>
          )}

          <div className="surface">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-neutral-200 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">
              <div className="col-span-2">Ref</div>
              <div className="col-span-2">Date</div>
              <div className="col-span-3">Description</div>
              <div className="col-span-1">Category</div>
              <div className="col-span-2 text-right">Amount</div>
              <div className="col-span-1 text-center">Edit</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            <div className="divide-y divide-neutral-200">
              {filtered.map((t) => {
                const rem = remainingMs(t.createdAt, t.locked)
                const editable = rem > 0 && !t.locked && !isFiscallyLocked(t.date)
                const isLocked = t.locked || isFiscallyLocked(t.date) || rem === 0
                return (
                  <div key={t.id} className="grid grid-cols-12 px-5 py-3 items-center text-sm">
                    <div className="col-span-2 font-mono text-xs text-neutral-600">{t.id}</div>
                    <div className="col-span-2 font-mono text-xs text-neutral-600">{formatDate(t.date, dateFormat)}</div>
                    <div className="col-span-3 text-ink flex items-center gap-1.5">
                      {isFiscallyLocked(t.date) && <Lock size={11} className="text-neutral-400" />}
                      {t.description}
                      {t.receipt_url && (
                        <button onClick={() => viewReceipt(t)} className="text-brand hover:text-brand-dark" title="View receipt">
                          <Paperclip size={11} />
                        </button>
                      )}
                    </div>
                    <div className="col-span-1 text-xs text-neutral-500">{t.category || '—'}</div>
                    <div className={`col-span-2 text-right font-mono text-xs ${t.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {formatCurrency(t.amount, currency)}
                    </div>
                    <div className="col-span-1 text-center">
                      {isFiscallyLocked(t.date) ? (
                        <Lock size={13} className="text-neutral-300 mx-auto" />
                      ) : t.locked || rem === 0 ? (
                        <Lock size={13} className="text-neutral-400 mx-auto" />
                      ) : (
                        <span className="font-mono text-[10px] text-amber-700">{fmtCountdown(rem)}</span>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end gap-2">
                      {editable ? (
                        <>
                          <button
                            onClick={() => editTransaction(t.id, { description: `${t.description} (edited)` })}
                            className="text-neutral-400 hover:text-brand"
                            aria-label="Edit"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => deleteTransaction(t.id)}
                            className="text-neutral-400 hover:text-rose-600"
                            aria-label="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      ) : isLocked && role === 'admin' ? (
                        <button
                          onClick={() => openCorrection(t)}
                          className="text-[10px] font-semibold text-brand hover:text-brand-dark border border-brand px-2 py-1 whitespace-nowrap"
                          title="Post a correction journal entry"
                        >
                          Correct Entry
                        </button>
                      ) : (
                        <Lock size={13} className="text-neutral-300" />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {role === 'admin' && (
            <p className="text-xs text-muted mt-5">
              Need to correct a locked transaction? Use the <span className="font-semibold text-brand">Correct Entry</span> button to post an official Correction Journal Entry that references the original TXN ID.
            </p>
          )}
        </div>
      </div>
    </>
  )

  // ============================================================
  // PAGE MODE - Renders as a normal page (no overlay)
  // ============================================================
  if (isPageMode) {
    return (
      <div className="flex flex-col bg-white rounded-lg overflow-hidden">
        {/* Header without close button */}
        <div className="shrink-0 bg-surface border-b border-neutral-200 flex items-center justify-between px-4 sm:px-6 py-3 gap-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">Historical Ledger</h2>
            <p className="text-[11px] text-muted">{filtered.length} of {transactions.length} entries · {filter} view</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={exportCsv} className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2" disabled={filtered.length === 0}>
              <Download size={13} /> <span className="hidden sm:inline">Export CSV</span>
            </button>
            {role === 'admin' && (
              <button onClick={() => setShowAddForm((s) => !s)} className="btn-primary text-xs flex items-center gap-1.5 px-3 py-2">
                <FileText size={13} /> <span className="hidden sm:inline">{showAddForm ? 'Close Form' : 'Add Entry'}</span>
              </button>
            )}
          </div>
        </div>

        {renderLedgerContent()}

        {/* Correction Modal - still needed in page mode */}
        {correction && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-ink/40" onClick={() => setCorrection(null)} aria-hidden />
            <div className="relative bg-surface border border-neutral-200 w-full max-w-lg animate-fade-in">
              <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                  <FileText size={15} /> Correction Journal Entry
                </h2>
                <button onClick={() => setCorrection(null)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="border border-neutral-200 bg-neutral-50 px-4 py-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted">Original Transaction</span>
                    <span className="font-mono font-semibold text-ink">{correction.id}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted">Description</span>
                    <span className="text-ink text-right max-w-[60%] truncate">{correction.description}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted">Original Amount</span>
                    <span className={`font-mono font-semibold ${correction.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {formatCurrency(correction.amount, currency)}
                    </span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted">Date</span>
                    <span className="font-mono text-ink">{formatDate(correction.date, dateFormat)}</span>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200">
                  <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    The original entry is locked and cannot be edited or deleted directly. A correction posts an offsetting balancing transaction that references the original TXN ID for audit traceability.
                  </p>
                </div>

                <form onSubmit={submitCorrection} className="space-y-4">
                  <div>
                    <label className="label-above">Correction Type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Full Reversal', 'Partial Adjustment'].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setCorrectionForm({ ...correctionForm, type: t })}
                          className={`px-3 py-2.5 text-xs font-semibold border transition-colors ${
                            correctionForm.type === t
                              ? 'border-brand bg-brand-soft text-brand'
                              : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted mt-1.5">
                      {correctionForm.type === 'Full Reversal'
                        ? 'Posts an offsetting entry that fully reverses the original amount.'
                        : 'Posts a partial offsetting entry. Specify the adjustment amount below.'}
                    </p>
                  </div>

                  {correctionForm.type === 'Partial Adjustment' && (
                    <div>
                      <label className="label-above">Adjustment Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        value={correctionForm.amount}
                        onChange={(e) => setCorrectionForm({ ...correctionForm, amount: e.target.value })}
                        className="input-flat font-mono"
                        placeholder="0.00"
                        min="0"
                      />
                      <p className="text-[11px] text-muted mt-1">The system will post an offsetting entry of this amount against the original.</p>
                    </div>
                  )}

                  <div>
                    <label className="label-above">Auditable Reason <span className="text-rose-600 normal-case">*</span></label>
                    <textarea
                      value={correctionForm.reason}
                      onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                      className="input-flat min-h-[80px] resize-none"
                      placeholder="Provide a clear reason for this correction. This will be permanently recorded in the audit trail."
                      required
                    />
                  </div>

                  <div>
                    <label className="label-above">Supporting Receipt (optional)</label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => setReceiptFile(e.target.files[0])}
                      className="hidden"
                      id="correction-receipt"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2"
                    >
                      <Upload size={13} /> {receiptFile ? receiptFile.name : 'Attach supporting document'}
                    </button>
                    {receiptFile && (
                      <button type="button" onClick={() => { setReceiptFile(null); if (fileRef.current) fileRef.current.value = '' }} className="ml-2 text-xs text-rose-600 hover:text-rose-700">
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button type="button" onClick={() => setCorrection(null)} className="btn-secondary flex-1">Cancel</button>
                    <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-1.5" disabled={correctionSaving}>
                      {correctionSaving ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                      Post Correction Entry
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ============================================================
  // OVERLAY MODE - Renders as a full-screen overlay
  // ============================================================
  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-neutral-50 animate-fade-in">
      <div className="shrink-0 bg-surface border-b border-neutral-200 flex items-center justify-between px-4 sm:px-6 py-3 gap-2">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-ink">Historical Ledger</h2>
          <p className="text-[11px] text-muted">{filtered.length} of {transactions.length} entries · {filter} view</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={exportCsv} className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2" disabled={filtered.length === 0}>
            <Download size={13} /> <span className="hidden sm:inline">Export CSV</span>
          </button>
          {role === 'admin' && (
            <button onClick={() => setShowAddForm((s) => !s)} className="btn-primary text-xs flex items-center gap-1.5 px-3 py-2">
              <FileText size={13} /> <span className="hidden sm:inline">{showAddForm ? 'Close Form' : 'Add Entry'}</span>
            </button>
          )}
          <button onClick={onClose} className="text-neutral-400 hover:text-ink" aria-label="Close">
            <X size={20} />
          </button>
        </div>
      </div>

      {renderLedgerContent()}

      {/* Correction Modal - same as page mode */}
      {correction && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setCorrection(null)} aria-hidden />
          <div className="relative bg-surface border border-neutral-200 w-full max-w-lg animate-fade-in">
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
              <h2 className="text-sm font-semibold text-ink flex items-center gap-2">
                <FileText size={15} /> Correction Journal Entry
              </h2>
              <button onClick={() => setCorrection(null)} className="text-neutral-400 hover:text-ink"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="border border-neutral-200 bg-neutral-50 px-4 py-3 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Original Transaction</span>
                  <span className="font-mono font-semibold text-ink">{correction.id}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Description</span>
                  <span className="text-ink text-right max-w-[60%] truncate">{correction.description}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Original Amount</span>
                  <span className={`font-mono font-semibold ${correction.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {formatCurrency(correction.amount, currency)}
                  </span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted">Date</span>
                  <span className="font-mono text-ink">{formatDate(correction.date, dateFormat)}</span>
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200">
                <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800">
                  The original entry is locked and cannot be edited or deleted directly. A correction posts an offsetting balancing transaction that references the original TXN ID for audit traceability.
                </p>
              </div>

              <form onSubmit={submitCorrection} className="space-y-4">
                <div>
                  <label className="label-above">Correction Type</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['Full Reversal', 'Partial Adjustment'].map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setCorrectionForm({ ...correctionForm, type: t })}
                        className={`px-3 py-2.5 text-xs font-semibold border transition-colors ${
                          correctionForm.type === t
                            ? 'border-brand bg-brand-soft text-brand'
                            : 'border-neutral-200 text-neutral-500 hover:border-neutral-300'
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-muted mt-1.5">
                    {correctionForm.type === 'Full Reversal'
                      ? 'Posts an offsetting entry that fully reverses the original amount.'
                      : 'Posts a partial offsetting entry. Specify the adjustment amount below.'}
                  </p>
                </div>

                {correctionForm.type === 'Partial Adjustment' && (
                  <div>
                    <label className="label-above">Adjustment Amount</label>
                    <input
                      type="number"
                      step="0.01"
                      value={correctionForm.amount}
                      onChange={(e) => setCorrectionForm({ ...correctionForm, amount: e.target.value })}
                      className="input-flat font-mono"
                      placeholder="0.00"
                      min="0"
                    />
                    <p className="text-[11px] text-muted mt-1">The system will post an offsetting entry of this amount against the original.</p>
                  </div>
                )}

                <div>
                  <label className="label-above">Auditable Reason <span className="text-rose-600 normal-case">*</span></label>
                  <textarea
                    value={correctionForm.reason}
                    onChange={(e) => setCorrectionForm({ ...correctionForm, reason: e.target.value })}
                    className="input-flat min-h-[80px] resize-none"
                    placeholder="Provide a clear reason for this correction. This will be permanently recorded in the audit trail."
                    required
                  />
                </div>

                <div>
                  <label className="label-above">Supporting Receipt (optional)</label>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setReceiptFile(e.target.files[0])}
                    className="hidden"
                    id="correction-receipt"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="btn-secondary text-xs flex items-center gap-1.5 px-3 py-2"
                  >
                    <Upload size={13} /> {receiptFile ? receiptFile.name : 'Attach supporting document'}
                  </button>
                  {receiptFile && (
                    <button type="button" onClick={() => { setReceiptFile(null); if (fileRef.current) fileRef.current.value = '' }} className="ml-2 text-xs text-rose-600 hover:text-rose-700">
                      Remove
                    </button>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setCorrection(null)} className="btn-secondary flex-1">Cancel</button>
                  <button type="submit" className="btn-primary flex-1 flex items-center justify-center gap-1.5" disabled={correctionSaving}>
                    {correctionSaving ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                    Post Correction Entry
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}